import {formatReferenceCopy} from 'utils/typography';
import React from "react";
import cx from "classnames";
import {useTranslation} from "react-i18next";
import {useFormContext} from "react-hook-form";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";

import {COLORS} from "../../../../../../../../../config/constants";

import {
  setBrokerDataMutation,
  useLazyGetBrokerDataByCodeQuery,
} from "../../../../../../../../../api/calc";
import {useGetCalcIdFromRoute} from "../../../../../../../../../utils/hooks";

import {isCalculationViewModeError, numberWithSpaces} from "../../../../../../../../../utils/helpers";
import Documents from "./components/documents";

import './certificate-company-card.scss'
import {BROKER_ACTIONS} from "../../../../../../../../../utils/constants";

const CompanyCard = ({ isActive, center, currentCenter, onClose, code, countryCode, techReglament } : CertificationCompanyCardProps ) => {

  const { t } = useTranslation()

  const orderId = useGetCalcIdFromRoute()

  const [ triggerGetBrokerDataByCode ] = useLazyGetBrokerDataByCodeQuery();

  const [ setBrokerData ] = setBrokerDataMutation()

  const { getValues } = useFormContext<CertificationCodeCuTrForm>();

  const handleChoose = async () => {

    if (!center) {
      return;
    }

    const { certification_scheme, declaration_type, document_duration } = getValues();

    const result = await setBrokerData({
      orderId,
      code: code,
      countryCode: countryCode,
      has_certificate: false,
      broker_tariff: center.uuid,
      action: BROKER_ACTIONS.CERT_FIND_COST,
      tech_reglament: techReglament,
      cert_params: {
        declaration_type,
        ...(certification_scheme ? { certification_scheme } : {}),
        ...(document_duration ? { document_duration } : {}),
      }
    });

    if (isCalculationViewModeError(result)) {
      return;
    }

    triggerGetBrokerDataByCode(orderId);
    if (onClose) {
      onClose();
    }

  }

  const name = center?.company?.name || currentCenter?.company?.name;

  const costTotal = center?.cost_total || currentCenter?.cost_total;
  const costTotalCurrency = center?.currency || currentCenter?.currency;

  const getDocumentsAndCertParams = (center?: any) => {
    const documents =
        center?.document_prices ||
        center?.documents ||
        [];

    if (!center || !documents.length) return [];

    const certParams = center.cert_params;

    return documents.map((document: any) => ({
      ...document,

      documents: [
        {
          name:
              document.tariff_service_description ||
              document.service_description,
        },

        ...(certParams?.declaration_type
          ? [
            {
              name: t('dictionaries__text__certification__typeOfDeclaration'),
              value: certParams.declaration_type.name,
            },
          ]
          : []),

        ...(certParams?.certification_scheme
          ? [
            {
              name: t('dictionaries__text__certification__certificationScheme'),
              value: certParams.certification_scheme.name,
            },
          ]
          : []),

        ...(certParams?.document_duration
          ? [
            {
              name: t('dictionaries__text__certification__termOfTheCertificate'),
              value: certParams.document_duration.name,
            },
          ]
          : []),
      ],
    }));
  };

  const documents = getDocumentsAndCertParams(center || currentCenter);

  return <Card className={cx('certificate-company-card', {
    'certificate-company-card_active': isActive,
  })} sx={{ background: isActive ? COLORS.LIME : COLORS.WHITE, borderRadius: 0, boxShadow: 'none' }}>
    <CardContent sx={{
      padding: 0, '&:last-child': {
        paddingBottom: 0
      }
    }}>
      <div className='certificate-company-card__main-content'>
        <div className='certificate-company-card__left'>
          <Typography component='span' variant='inherit' className='certificate-company-card__logo' sx={{
            fontSize: '24px',
            lineHeight: '36px',
            color: COLORS.WHITE,
          }}>{name?.[0]}</Typography>
          <Typography variant='subtitle1' sx={{
            fontSize: '16px',
            lineHeight: '24px',
            fontWeight: 500,
            color: COLORS.BLACK_DARK,
          }}>
            {name && formatReferenceCopy(name)}
          </Typography>
        </div>
        <div className={cx('certificate-company-card__right', {
          'certificate-company-card__right_active': isActive,
        })}>
          <Typography variant='h6' sx={{
            fontSize: '20px',
            lineHeight: '26px',
            fontWeight: 400,
            color: COLORS.BLACK_DARK,
            flexShrink: 0,
          }}>
            {`${numberWithSpaces(costTotal)} ${costTotalCurrency}`}
          </Typography>
          {!isActive && <Button
            variant='outlined'
            fullWidth
            sx={{
              width: 'auto',
              flexShrink: 0,
              '&.Mui-disabled': {
                backgroundColor: COLORS.SLIGHTLY_GRAY,
                color: COLORS.LIGHT_TEXT_DISABLED_OPACITY,
                border: 'none'
              }
            }}
            onClick={handleChoose}
          >
            <Typography component='span' variant='inherit'>{formatReferenceCopy(t('actions__text__choose'))}</Typography>
          </Button>}
        </div>
      </div>
      <Divider/>
      {documents?.map((document: HsCodeOutputItemDocumentPricesItem, index: number) => <Documents key={index} document={document} />)}
    </CardContent>
  </Card>
}

export default CompanyCard
